import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, MessageCircle, Gift, Phone, X } from 'lucide-react';

const StickyCTA = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showCallPopup, setShowCallPopup] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show sticky CTA after scrolling 300px
      if (window.scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToBooking = () => {
    const bookingSection = document.getElementById('booking-section');
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToLocation = () => {
    const bookingSection = document.getElementById('booking-section');
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: 'smooth' });
      // Focus on location input after scroll
      setTimeout(() => {
        const locationInput = document.querySelector('input[name="location"]');
        if (locationInput) locationInput.focus();
      }, 800);
    }
  };

  const scrollToRefer = () => {
    const referSection = document.getElementById('refer-earn');
    if (referSection) {
      referSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const openChat = () => {
    // In production, this would open a chat widget
    window.open('https://wa.me/919876543210?text=Hi, I want to know more about Cleansing Therapy', '_blank');
  };

  const handleCall = () => {
    setShowCallPopup(true);
  };

  return (
    <>
      {/* Sticky Bottom Bar (Desktop) */}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="hidden md:block fixed bottom-0 left-0 right-0 z-50 bg-white shadow-2xl border-t border-gray-200"
          >
            <div className="max-w-7xl mx-auto px-6 py-4">
              <div className="flex items-center justify-between gap-4">
                {/* Left: Quick Info */}
                <div>
                  <p className="text-sm text-gray-600">Start Your Wellness Journey</p>
                  <p className="text-xl font-bold text-gray-800">
                    Book Your <span className="text-primary">First Cleanse</span> Today
                  </p>
                </div>

                {/* Right: CTA Buttons */}
                <div className="flex items-center gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleCall}
                    className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    Call Us
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={scrollToBooking}
                    className="px-8 py-3 bg-gradient-to-r from-primary to-purple-600 text-white rounded-lg font-bold flex items-center gap-2 shadow-lg hover:shadow-xl transition-all"
                  >
                    Book Now →
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Action Buttons (Mobile) */}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
            className="md:hidden fixed bottom-6 right-4 z-50 flex flex-col gap-3"
          >
            {/* Location FAB */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={scrollToLocation}
              className="w-14 h-14 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center"
            >
              <MapPin className="w-6 h-6" />
            </motion.button>

            {/* Chat FAB */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={openChat}
              className="w-14 h-14 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg flex items-center justify-center"
            >
              <MessageCircle className="w-6 h-6" />
            </motion.button>

            {/* Refer FAB */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={scrollToRefer}
              className="w-14 h-14 bg-pink-500 hover:bg-pink-600 text-white rounded-full shadow-lg flex items-center justify-center"
            >
              <Gift className="w-6 h-6" />
            </motion.button>

            {/* Main Book Now FAB */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={scrollToBooking}
              className="w-16 h-16 bg-gradient-to-r from-primary to-purple-600 text-white rounded-full shadow-2xl flex items-center justify-center font-bold text-lg"
            >
              📅
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Call Popup Modal */}
      <AnimatePresence>
        {showCallPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCallPopup(false)}
          >
            <motion.div
              initial={{ scale: 0.8, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl relative"
            >
              <button
                onClick={() => setShowCallPopup(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-primary to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Phone className="w-10 h-10 text-white" />
                </div>

                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Call Our Wellness Experts
                </h3>

                <p className="text-gray-600 mb-6">
                  Available 9 AM - 9 PM, 7 days a week
                </p>

                <a
                  href="tel:+919876543210"
                  className="block w-full bg-gradient-to-r from-primary to-purple-600 text-white py-4 rounded-xl text-lg font-bold shadow-lg hover:shadow-xl transition-all"
                >
                  📞 +91 98765 43210
                </a>

                <p className="text-sm text-gray-500 mt-4">
                  Or continue booking online below
                </p>

                <button
                  onClick={() => {
                    setShowCallPopup(false);
                    scrollToBooking();
                  }}
                  className="mt-3 text-primary font-semibold hover:underline"
                >
                  Book Online →
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default StickyCTA;
